<template>
  <div
    :class="['upload', { 'upload--over': over, 'upload--off': disabled }]"
    @dragenter.prevent="onDrag"
    @dragover.prevent="onDrag"
    @dragleave.prevent="over = false"
    @drop.prevent="onDrop"
  >
    <div :class="['upload__preview', `upload__preview--${kind}`]">
      <img v-if="previewSrc && !broken" :src="previewSrc" alt="" @error="broken = true" />
      <span v-else class="xs subtle">{{ broken ? t("upload.noPreview") : t("upload.empty") }}</span>
    </div>

    <div class="upload__body">
      <template v-if="busy">
        <div class="upload__bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" :aria-valuenow="pct">
          <span :style="{ width: `${pct}%` }"></span>
        </div>
        <div class="row">
          <span class="xs muted">{{ t("upload.uploading", { pct }) }}</span>
          <button type="button" class="btn btn--ghost btn--sm" @click="cancel">{{ t("common.cancel") }}</button>
        </div>
      </template>
      <template v-else>
        <span class="small muted">{{ t("upload.drop") }}</span>
        <div class="row row--wrap">
          <button type="button" class="btn btn--secondary btn--sm" :disabled="disabled" @click="picker.click()">
            {{ modelValue ? t("upload.replace") : t("upload.choose") }}
          </button>
          <button v-if="modelValue && clearable" type="button" class="btn btn--ghost btn--sm" :disabled="disabled" @click="clear">
            {{ t("common.remove") }}
          </button>
          <button type="button" class="btn btn--ghost btn--sm" :disabled="disabled" :aria-expanded="showUrl" @click="showUrl = !showUrl">
            {{ showUrl ? t("upload.hideUrl") : t("upload.pasteUrl") }}
          </button>
        </div>
      </template>

      <input
        v-if="showUrl"
        :id="inputId || undefined"
        :value="modelValue"
        class="input"
        type="url"
        maxlength="500"
        placeholder="https://"
        :aria-label="label"
        :disabled="disabled || busy"
        :class="{ 'input--invalid': modelValue && !isHttps(modelValue) }"
        @input="emit('update:modelValue', $event.target.value.trim())"
      />
      <span v-if="error" class="field__error" role="alert">{{ error }}</span>
      <span v-else-if="uploaded && modelValue === uploaded" class="field__hint">{{ t("upload.done") }}</span>
      <span v-else class="field__hint">{{ t(`upload.rules.${kind}`, { max: formatBytes(MEDIA_RULES[kind].maxBytes) }) }}</span>
    </div>

    <input ref="picker" type="file" :accept="MEDIA_TYPES.join(',')" hidden @change="onPick" />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { api } from "../lib/api";
import { useErrorText } from "../lib/errors";
import { MEDIA_RULES, MEDIA_TYPES, checkImage, formatBytes, isHttps } from "../lib/media";

const props = defineProps({
  modelValue: { type: String, default: "" },
  appId: { type: String, required: true },
  kind: { type: String, required: true, validator: (k) => k in MEDIA_RULES },
  label: { type: String, default: "" },
  inputId: { type: String, default: "" },
  disabled: { type: Boolean, default: false },
  clearable: { type: Boolean, default: true }
});
const emit = defineEmits(["update:modelValue"]);
const { t } = useI18n();
const errorText = useErrorText();

const picker = ref(null);
const over = ref(false);
const busy = ref(false);
const pct = ref(0);
const error = ref("");
const broken = ref(false);
const showUrl = ref(Boolean(props.modelValue));
const uploaded = ref("");
// The just-uploaded file previews from memory: its public URL may sit behind a CDN or another host.
const localPreview = ref("");
let controller = null;

const previewSrc = computed(() => {
  if (localPreview.value && props.modelValue === uploaded.value) return localPreview.value;
  return isHttps(props.modelValue) ? props.modelValue : "";
});

watch(() => props.modelValue, () => {
  broken.value = false;
  if (props.modelValue !== uploaded.value) error.value = "";
});

function setPreview(url) {
  if (localPreview.value) URL.revokeObjectURL(localPreview.value);
  localPreview.value = url;
}

function onDrag() {
  if (!props.disabled && !busy.value) over.value = true;
}

function onDrop(e) {
  over.value = false;
  if (props.disabled || busy.value) return;
  const file = e.dataTransfer?.files?.[0];
  if (file) upload(file);
}

function onPick(e) {
  const file = e.target.files?.[0];
  e.target.value = "";
  if (file) upload(file);
}

async function upload(file) {
  error.value = "";
  const preview = URL.createObjectURL(file);
  const problem = await checkImage(file, props.kind, preview);
  if (problem) {
    URL.revokeObjectURL(preview);
    error.value = t(`upload.errors.${problem.key}`, problem.params);
    return;
  }
  busy.value = true;
  pct.value = 0;
  controller = new AbortController();
  try {
    const media = await api.uploadMedia(props.appId, props.kind, file, {
      signal: controller.signal,
      onProgress: (p) => (pct.value = Math.round(p * 100))
    });
    setPreview(preview);
    uploaded.value = media.url;
    showUrl.value = false;
    emit("update:modelValue", media.url);
  } catch (e) {
    URL.revokeObjectURL(preview);
    if (e.code !== "ABORTED") error.value = errorText(e);
  } finally {
    busy.value = false;
    controller = null;
  }
}

function cancel() {
  controller?.abort();
}

function clear() {
  setPreview("");
  uploaded.value = "";
  emit("update:modelValue", "");
}

onBeforeUnmount(() => {
  controller?.abort();
  setPreview("");
});
</script>

<style scoped>
.upload {
  display: flex;
  gap: var(--space-3);
  align-items: flex-start;
  padding: var(--space-3);
  border: 1px dashed var(--line-strong);
  border-radius: var(--radius);
  background: var(--bg-raised);
  transition: border-color var(--transition), background var(--transition);
}
.upload--over { border-color: var(--accent); background: var(--accent-soft); }
.upload--off { background: var(--bg-sunken); }
.upload__preview {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: var(--radius);
  background: var(--bg-sunken);
  text-align: center;
}
.upload__preview--icon { width: 64px; height: 64px; }
.upload__preview--screenshot { width: 128px; height: 80px; }
.upload__preview img { width: 100%; height: 100%; object-fit: contain; }
.upload__body { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: var(--space-2); }
.upload__body .field__hint, .upload__body .field__error { margin-top: 0; }
.upload__bar { height: 6px; border-radius: 3px; background: var(--bg-sunken); overflow: hidden; }
.upload__bar span { display: block; height: 100%; background: var(--accent); transition: width var(--transition); }
</style>
